import { useEffect, useId, useRef, useState } from "react";
import { RiDeleteBack2Line, RiEyeLine, RiEyeOffLine } from "react-icons/ri";
import { LuLockKeyhole } from "react-icons/lu";
import "./style.css";

export const PinPad = ({
  callback,
  inputText,
  head = "Acceso al TPV",
  allowText = false,
  digits = 4,
  platform = false,
  description,
  feedback = "",
}) => {
  const [pin, setPin] = useState("");
  const valueRef = useRef("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [visible, setVisible] = useState(false);
  const [legacyCode, setLegacyCode] = useState(false);
  const textEntry = allowText || legacyCode;
  const codeLabel = platform ? "Código de plataforma" : "PIN del empleado";
  const pending = useRef(false);
  const helperId = useId();
  const pinInput = useRef(null);
  useEffect(() => {
    if (!textEntry) pinInput.current?.focus();
  }, [textEntry]);
  const submit = async (value = valueRef.current) => {
    if (pending.current || !value || (!textEntry && value.length !== digits)) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      const accepted = await callback(value);
      if (accepted !== false) {
        valueRef.current = "";
        setPin("");
      }
    } catch {
      setError("No se pudo comprobar el acceso. Vuelve a intentarlo.");
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  const update = (value) => {
    const next = textEntry ? value : value.replace(/\D/g, "").slice(0, digits);
    if (next === valueRef.current) return;
    valueRef.current = next;
    setPin(next);
    setError("");
    if (!textEntry && next.length === digits) void submit(next);
  };
  return (
    <section className={`accessiblePinPad ${platform || allowText ? "tpvPlatformPassword" : "tpvEmployeePin"}`} aria-busy={busy}>
      <div className="tpvAccessSymbol" aria-hidden="true">
        <LuLockKeyhole />
      </div>
      <h1 className="loginTitle">{head}</h1>
      {description && <p className="tpvAccessIdentity">{description}</p>}
      <p className="tpvAccessHint" id={helperId}>
        {legacyCode ? "Introduce tu código anterior y pulsa Continuar." : inputText}
      </p>
      {(error || feedback) && (
        <p role="alert" className="tpvAccessError">
          {error || feedback}
        </p>
      )}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {textEntry ? (
          <div className="tpvPasswordField">
            <label htmlFor={`${helperId}-input`}>Código de acceso al TPV</label>
            <div>
              <input
                id={`${helperId}-input`}
                type={visible ? "text" : "password"}
                aria-label="Código de plataforma"
                aria-describedby={helperId}
                inputMode="none"
                autoComplete="current-password"
                value={pin}
                disabled={busy}
                onChange={(event) => update(event.target.value)}
              />
              <button
                type="button"
                aria-label={visible ? "Ocultar código" : "Mostrar código"}
                aria-pressed={visible}
                disabled={busy}
                onClick={() => setVisible((value) => !value)}
              >
                {visible ? <RiEyeOffLine /> : <RiEyeLine />}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="inputPwd">
              <div
                ref={pinInput}
                className="tpvPinInput"
                role="textbox"
                tabIndex={busy ? -1 : 0}
                aria-label={codeLabel}
                aria-describedby={`${helperId} ${helperId}-length`}
                aria-multiline="false"
                aria-disabled={busy}
                onKeyDown={(event) => {
                  if (pending.current || event.ctrlKey || event.metaKey || event.altKey) return;
                  if (/^\d$/.test(event.key)) {
                    event.preventDefault();
                    update(valueRef.current + event.key);
                  } else if (event.key === "Backspace") {
                    event.preventDefault();
                    update(valueRef.current.slice(0, -1));
                  } else if (event.key === "Delete" || event.key.toLowerCase() === "c") {
                    event.preventDefault();
                    update("");
                  } else if (event.key === "Enter") {
                    event.preventDefault();
                    void submit();
                  }
                }}
                onPaste={(event) => {
                  event.preventDefault();
                  if (!pending.current) update(event.clipboardData.getData("text"));
                }}
              />
              <span className="tpvPinDots" aria-hidden="true">
                {Array.from({ length: digits }, (_, index) => index).map((index) => (
                  <span key={index} className={index < pin.length ? "is-filled" : ""} />
                ))}
              </span>
              <span id={`${helperId}-length`} className="visually-hidden" role="status">
                {codeLabel}: {pin.length} de {digits} dígitos introducidos
              </span>
            </div>

          </>
        )}
            <div className="pinList" role="group" aria-label="Teclado de acceso">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((value) => (
                <button
                  key={value}
                  type="button"
                  className="cardPin"
                  aria-label={value}
                  disabled={busy}
                  onClick={() => update(valueRef.current + value)}
                >
                  {value}
                </button>
              ))}
              <button
                type="button"
                className="cardPin pinAuxiliary"
                aria-label="Borrar PIN"
                disabled={busy || !pin}
                onClick={() => update("")}
              >
                C
              </button>
              <button
                type="button"
                className="cardPin"
                aria-label="0"
                disabled={busy}
                onClick={() => update(valueRef.current + "0")}
              >
                0
              </button>
              <button
                type="button"
                className="cardPin pinAuxiliary"
                aria-label="Borrar último dígito"
                disabled={busy || !pin}
                onClick={() => update(valueRef.current.slice(0, -1))}
              >
                <RiDeleteBack2Line aria-hidden="true" />
              </button>
            </div>
        <button
          type="submit"
          className="tpvAccessPrimary"
          hidden={!textEntry && (pin.length !== digits || busy)}
          aria-label={platform || allowText ? "Continuar al TPV" : "Confirmar PIN"}
          disabled={busy || !pin || (!textEntry && pin.length !== digits)}
        >
          {busy ? "Comprobando acceso…" : textEntry ? "Continuar" : platform ? "Reintentar" : "Entrar"}
        </button>
        {busy && (
          <p role="status" className="tpvAccessStatus">
            Comprobando acceso…
          </p>
        )}
      </form>
      {platform && (
        <button
          type="button"
          className="tpvLegacyCode"
          disabled={busy}
          onClick={() => {
            valueRef.current = "";
            setPin("");
            setError("");
            setVisible(false);
            setLegacyCode((current) => !current);
          }}
        >
          {legacyCode ? "Usar código de 6 dígitos" : "Usar un código anterior de otra longitud"}
        </button>
      )}
    </section>
  );
};
